<template>

  <div>
    <div v-show="!showDetails" class="page">

      <!--Header-->
      <div class="page-header flex-shrink-0 mb-3 bg-white border-bottom">
        <div class="container-fluid d-flex justify-content-between px-md-4 px-3">
          <h3><i class="fas fa-address-card"></i>&nbsp;Vendors</h3>
          <div>
            <b-btn variant="success" @click="showNewVendorModal" v-show="!showDetails"><i class="fas fa-plus"></i>
              Vendor</b-btn>
          </div>
        </div>
      </div>

      <!--Search-->
      <div class="container-fluid px-md-4 px-3 py-3 bg-white border-bottom mb-3">
        <div class="row no-gutters">
          <div class="col-lg-4 col-md-6">
            <b-input-group>
              <b-form-input v-model="filter" type="search" id="filterInput" placeholder="Type to Search"></b-form-input>
            </b-input-group>
          </div>
          <b-button :disabled="!filter" variant="link" @click="filter = ''">Clear</b-button>
        </div>
      </div>

      <!--Content-->
      <div v-if="vendors" class="container-fluid flex-grow-1 overflow-auto px-md-4 px-3">
        <b-table id="vendors-table" bordered class="mb-5 rg-table" :items="vendorProvider"
          :fields="vendorTableData.fields" no-local-filtering :filter="filter" no-local-sorting
          :sortDesc.sync="sortDesc" :busy.sync="isBusy">
          <template v-slot:cell(name)="data">
            <div class="emphasis">
              {{ data.item.name }}
            </div>
          </template>
          <template v-slot:cell(type)="data">
            <div class="emphasis">
              {{ data.item.type }}
            </div>
          </template>
          <template v-slot:cell(email)="data">
            <div class="emphasis">
              {{ data.item.email }}
            </div>
          </template>
          <template v-slot:cell(phone)="data">
            <div class="emphasis">
              {{ data.item.phone }}
            </div>
          </template>
          <template v-slot:cell(address)="data">
            <div class="emphasis">
              {{ data.item.address.city }}, {{ data.item.address.state }}
            </div>
          </template>
          <template v-slot:cell(id)="data">
            <div class="emphasis">
              <b-btn title="Edit Vendor" @click="showEditVendorModal(data.item.id)" variant="link"><i
                  class="fas fa-edit"></i></b-btn>
            </div>
          </template>
        </b-table>
      </div>
    </div>

    <div v-show="showDetails" class="pb-5 px-md-3">
      <ValidationObserver v-slot="{ invalid }" ref="formValidationObserver">
        <b-form @submit.prevent="SaveVendor">
          <!--Header-->
          <div class="page-header">
            <div class="container d-flex justify-content-between p-0">
              <h3><i class="fas fa-house-user"></i>&nbsp;&nbsp;Add/Edit Vendors</h3>
              <div class="right menu">
                <b-btn variant="light" @click="toggleModal" class="mr-1">Close</b-btn>
                <b-btn type="submit" variant="primary" :disabled="invalid">Save</b-btn>
              </div>
            </div>
          </div>

          <div class="container form-container p-md-4 p-3">
            <!--Vendor Details-->
            <label class="section-label"><i class="fas fa-info-circle"></i> Vendor Details</label>
            <div v-if="vendorForEdit" class="mb-5">
              <div class="form-group row">
                <div class="col-md-6 col-12">
                  <label class="control-label">Name</label>
                  <ValidationProvider name="Name" rules="required" v-slot="validation">
                    <b-form-input placeholder="Name" v-model="vendorForEdit.name" name="Name"
                      :state="spr2_getValidationState(validation)" aria-describedby="name-feedback" />
                    <b-form-invalid-feedback id="name-feedback">{{ validation.errors[0] }}</b-form-invalid-feedback>
                  </ValidationProvider>
                </div>
                <div class="col-md-6 col-12">
                  <label class="control-label">Email Address</label>
                  <ValidationProvider name="Email" rules="email" v-slot="validation">
                    <b-form-input placeholder="Email" v-model="vendorForEdit.email" name="Email Address"
                      :state="spr2_getValidationState(validation)" aria-describedby="email-feedback" />
                    <b-form-invalid-feedback id="email-feedback">{{ validation.errors[0] }}</b-form-invalid-feedback>
                  </ValidationProvider>
                </div>
              </div>
              <div class="form-group row">
                <div class="col-12">
                  <label class="control-label">Type</label>
                  <b-form-select selection :options="vendorTypesOptions" v-model="vendorForEdit.typeId" class="d-block">
                    <template #first>
                      <b-form-select-option :value="null" disabled>Type</b-form-select-option>
                    </template>
                  </b-form-select>
                </div>
              </div>
              <div class="form-group row">
                <div class="col-12">
                  <label class="control-label">Street Address 1 *</label>
                  <b-form-input
                    placeholder="Street Address 1"
                    v-model="vendorForEdit.address.streetAddress1"
                    :state="vendorAddressErrors.streetAddress1 ? false : null"
                    aria-describedby="street1-feedback"
                  />
                  <b-form-invalid-feedback id="street1-feedback" :force-show="!!vendorAddressErrors.streetAddress1">{{ vendorAddressErrors.streetAddress1 }}</b-form-invalid-feedback>
                </div>
              </div>
              <div class="form-group row">
                <div class="col-12">
                  <label class="control-label">Street Address 2</label>
                  <b-form-input
                    placeholder="Street Address 2"
                    v-model="vendorForEdit.address.streetAddress2"
                  />
                </div>
              </div>
              <div class="form-group row">
                <div class="col-md-5 col-12">
                  <label class="control-label">City *</label>
                  <b-form-input
                    placeholder="City"
                    v-model="vendorForEdit.address.city"
                    :state="vendorAddressErrors.city ? false : null"
                    aria-describedby="city-feedback"
                  />
                  <b-form-invalid-feedback id="city-feedback" :force-show="!!vendorAddressErrors.city">{{ vendorAddressErrors.city }}</b-form-invalid-feedback>
                </div>
                <div class="col-md-4 col-12">
                  <label class="control-label">State *</label>
                  <b-form-select
                    v-model="vendorForEdit.address.state"
                    :options="filteredVendorStateOptions"
                    :state="vendorAddressErrors.state ? false : null"
                  >
                    <template #first>
                      <b-form-select-option value="">Select state&hellip;</b-form-select-option>
                    </template>
                  </b-form-select>
                  <b-form-invalid-feedback :force-show="!!vendorAddressErrors.state">{{ vendorAddressErrors.state }}</b-form-invalid-feedback>
                </div>
                <div class="col-md-3 col-12">
                  <label class="control-label">Country *</label>
                  <b-form-select
                    v-model="vendorForEdit.address.country"
                    :options="countryOptions"
                    :state="vendorAddressErrors.country ? false : null"
                    @change="onVendorCountryChange"
                  >
                    <template #first>
                      <b-form-select-option value="">Select country&hellip;</b-form-select-option>
                    </template>
                  </b-form-select>
                  <b-form-invalid-feedback :force-show="!!vendorAddressErrors.country">{{ vendorAddressErrors.country }}</b-form-invalid-feedback>
                </div>
              </div>
              <div class="form-group row">
                <div class="col-md-6 col-12">
                  <label class="control-label">Phone Number</label>
                  <ValidationProvider name="Phone Number" rules="phoneNumber" v-slot="validation">
                    <b-form-input placeholder="(555) 555-5555" v-model="vendorForEdit.phone"
                      :state="spr2_getValidationState(validation)" aria-describedby="phone-feedback" />
                    <b-form-invalid-feedback id="phone-feedback">{{ validation.errors[0] }}</b-form-invalid-feedback>
                  </ValidationProvider>
                </div>
              </div>

              <!--Properties-->
              <div class="row mt-5">
                <div class="col-md-6 col-12">
                  <label class="section-label d-block mb-0"><i class="fas fa-home"></i> Properties</label>
                  <small>Select properties to associate with this vendor</small>
                </div>
                <div class="col-md-6 col-12 d-flex align-items-end justify-content-end">
                  <b-form-checkbox v-model="vendorForEdit.allPropertiesAssociated">
                    Associate to ALL Properties
                  </b-form-checkbox>
                </div>
              </div>
              <div :disabled="vendorForEdit.allPropertiesAssociated" class="mt-2">
                <div class="row align-items-center no-gutters rg-table-add">
                  <div class="flex-grow-1">
                    <b-form-select class="w-100" :disabled="vendorForEdit.allPropertiesAssociated" selection
                      :options="propertiesOptions" v-model="modalSelectedProperty">
                      <template #first>
                        <b-form-select-option :value="null" disabled>Select a Property to
                          Associate</b-form-select-option>
                      </template>
                    </b-form-select>
                  </div>
                  <div class="flex-shrink-1">
                    <b-btn variant="success" @click="addPropertyToList"
                      :disabled="vendorForEdit.allPropertiesAssociated">Add</b-btn>
                  </div>
                </div>
                <b-table-simple bordered class="mb-0 rg-table">
                  <b-thead>
                    <b-tr>
                      <b-th>Name</b-th>
                      <b-th>Location</b-th>
                      <b-th class="text-center" style="width: 10%;">Remove</b-th>
                    </b-tr>
                  </b-thead>
                  <b-tbody>
                    <b-tr v-for="prop in vendorForEditProperties" v-bind:key="prop.id">
                      <b-td>
                        <div>
                          {{ prop.name }}
                        </div>
                      </b-td>
                      <b-td>
                        <div>
                          {{ prop.city }} {{ prop.state }}
                        </div>
                      </b-td>
                      <b-td class="p-1 text-center">
                        <b-btn class="mt-1" variant="light" @click="deletePropertyAssociation(prop.id)"
                          :disabled="vendorForEdit.allPropertiesAssociated"><i class="fas fa-times"></i></b-btn>
                      </b-td>
                    </b-tr>
                  </b-tbody>
                </b-table-simple>
              </div>

              <!--Property Groups-->
              <label class="section-label d-block mt-5 mb-0"><i class="fas fa-warehouse"></i> Property Groups</label>
              <small>Select property groups to associate with this vendor</small>
              <div class="row mt-2 no-gutters rg-table-add">
                <div class="flex-grow-1">
                  <b-form-select class="w-100" :options="propertyGroupOptions" v-model="selectedPropertyGroup">
                    <template #first>
                      <b-form-select-option :value="null" disabled>Select a Property Group to
                        Associate</b-form-select-option>
                    </template>
                  </b-form-select>
                </div>
                <div class="flex-shrink-1">
                  <b-btn variant="success" @click="addPropertyGroupToList">Add</b-btn>
                </div>
              </div>
              <b-table-simple bordered class="rg-table mb-5">
                <b-thead head-variant="dark">
                  <b-tr>
                    <b-th>Name</b-th>
                    <b-th class="text-center" style="width: 10%;">Remove</b-th>
                  </b-tr>
                </b-thead>
                <b-tbody>
                  <b-tr v-for="prop in vendorForEditPropertyGroups" v-bind:key="prop.id">
                    <b-td>
                      <div>
                        {{ prop.name }}
                      </div>
                    </b-td>
                    <b-td class="p-1 text-center">
                      <b-btn class="mt-1" variant="light" @click="deletePropertyGroupAssociation(prop.id)"><i
                          class="fas fa-times"></i></b-btn>
                    </b-td>
                  </b-tr>
                </b-tbody>
              </b-table-simple>
            </div>
          </div>
        </b-form>
      </ValidationObserver>
    </div>
  </div>
</template>
<script>
import { mapActions } from 'vuex'
import { spr2Validation } from '../../mixins/Spr2SharedFunctions'
export default {
  name: 'Vendors',
  mixins: [ spr2Validation ],
  created () {
    this.fields = [
      {key: 'name', label: 'Name', sortable: true},
      {key: 'type', label: 'Type', sortable: true},
      {key: 'email', label: 'Email', sortable: true},
      {key: 'phone', label: 'Phone', sortable: true},
      {key: 'address', label: 'Location', sortable: true},
      {key: 'id', label: 'Actions'}]
    this.doLoadVendorTypes()
    this.loadProperties()
    this.doLoadPropertyGroups()
    this.getLookupStates()
    this.getLookupCountries()
  },
  components: {
  },
  data () {
    return {
      fields: [],
      vendors: [],
      isBusy: false,
      sortBy: null,
      sortDirection: 'asc',
      filter: null,
      sortDesc: true, // End new
      showDetails: false,
      modalSelectedProperty: null,
      propertyGroups: [],
      vendorForEdit: null,
      selectedPropertyGroup: null,
      vendorForEditProperties: [],
      vendorForEditPropertyGroups: [],
      vendorTypes: [],
      vendorAddressErrors: { streetAddress1: null, city: null, state: null, country: null }
    }
  },
  computed: {
    // vendors () {
    //   return this.$store.state.company.vendors
    // },
    vendorTableData () {
      var returnVendors = {
        fields: this.fields
      }
      console.log(
        'Event Table Data is: ' +
          JSON.stringify(returnVendors, null, 2)
      )
      return returnVendors
    },
    allCompanyProperties () {
      return this.$store.state.company.properties
    },
    countryOptions () {
      // Countries from the API already have { value, text } keys (camelCase from C# Country model).
      // Return the raw array directly — no remapping needed.
      return this.$store.state.lookups.countries || []
    },
    stateOptions () {
      return (this.$store.state.lookups.states || [])
        .filter(s => s.country === 'USA')
        .map(s => ({ value: s.abbreviation, text: s.name }))
    },
    filteredVendorStateOptions () {
      const country = this.vendorForEdit && this.vendorForEdit.address ? this.vendorForEdit.address.country : 'USA'
      return (this.$store.state.lookups.states || [])
        .filter(s => s.country === (country || 'USA'))
        .map(s => ({ value: s.abbreviation, text: s.name }))
    },
    vendorTypesOptions () {
      var types = this.vendorTypes.filter(a => a.active === true)
      var results = []
      results = types.map((t) => { return { text: t.name, value: t.id } })
      // console.log('Returning Vendor Types Options: ' + JSON.stringify(results, null, 2))
      return results
    },
    propertiesOptions () {
      var properties = this.allCompanyProperties
      var results = []
      results = properties.map((p) => { return { text: p.name, value: p.id } })
      console.log('Returning Property Options: ' + JSON.stringify(results, null, 2))
      return results
    },
    propertyGroupOptions () {
      var propertyGroups = this.propertyGroups
      var results = propertyGroups.map((group) => { return {text: group.name, value: group.id} })
      console.log('Returning Property Options: ' + JSON.stringify(results, null, 2))
      return results
    }
  },
  methods: {
    ...mapActions('company', ['getVendors', 'saveVendor', 'getVendorForEdit', 'loadVendorForNew', 'getVendorTypes', 'loadProperties', 'getPropertyGroups']),
    ...mapActions('lookups', ['getLookupStates', 'getLookupCountries']),
    vendorProvider (ctx) {
      this.isBusy = true
      console.log(
        'vendor table ctx is: ' +
          JSON.stringify(ctx, null, 2)
      )
      console.log('DESC', ctx.sortDesc)
      var request = { sortBy: ctx.sortBy, sortDirection: ctx.sortDesc ? 'desc' : 'asc', search: ctx.filter }

      // Must return a promise that resolves to an array of items
      return this.getVendors(request).then(r => {
        // Pluck the array of items off our axios response
        const items = r.data.vendors
        console.log(
          'Returned Table Data is: ' +
          JSON.stringify(items, null, 2)
        )
        this.isBusy = false
        // Must return an array of items or an empty array if an error occurred
        return items || []
      })
    },
    toggleModal () {
      this.showDetails = !this.showDetails
    },
    showEditVendorModal (id) {
      this.getVendorForEdit(id)
        .then(result => {
          const vendor = result.data.vendor
          // Normalize address: existing vendors saved before address validation was added
          // may have address: null.  Ensure address is always a plain object so template
          // bindings (vendorForEdit.address.city / .state) never throw a TypeError.
          if (!vendor.address) {
            vendor.address = { streetAddress1: '', streetAddress2: '', city: '', state: '', country: 'USA' }
          } else {
            vendor.address.streetAddress1 = vendor.address.streetAddress1 || ''
            vendor.address.streetAddress2 = vendor.address.streetAddress2 || ''
            vendor.address.city = vendor.address.city || ''
            vendor.address.state = vendor.address.state || ''
            vendor.address.country = vendor.address.country || 'USA'
          }
          if (vendor.allPropertiesAssociated == null) vendor.allPropertiesAssociated = false
          this.vendorForEdit = vendor
          this.vendorForEditProperties = result.data.propertyAssociations
          this.vendorForEditPropertyGroups = result.data.propertyGroupAssociations
        })
        .then(() => this.doLoadPropertyGroups()
          .then(() => {
            this.vendorAddressErrors = { streetAddress1: null, city: null, state: null, country: null }
            this.$nextTick(() => {
              if (this.$refs.formValidationObserver) this.$refs.formValidationObserver.reset()
            })
            this.showDetails = true
          }))
    },
    showNewVendorModal (id) {
      this.vendorForEdit = {name: '', type: 'Disaster Recovery', email: '', phone: '', location: '', address: {streetAddress1: '', streetAddress2: '', city: '', state: '', country: 'USA'}, propertyAssociations: [], propertyGroupAssociations: [], allPropertiesAssociated: false}
      this.vendorAddressErrors = { streetAddress1: null, city: null, state: null, country: null }
      this.$nextTick(() => {
        if (this.$refs.formValidationObserver) this.$refs.formValidationObserver.reset()
      })
      this.toggleModal()
    },
    onVendorCountryChange () {
      // Clear state when country changes — the previously selected state may not exist in the new country's list
      if (this.vendorForEdit && this.vendorForEdit.address) {
        this.vendorForEdit.address.state = ''
      }
      this.vendorAddressErrors.state = null
    },
    validateVendorAddress () {
      const addr = this.vendorForEdit ? this.vendorForEdit.address : null
      this.vendorAddressErrors = { streetAddress1: null, city: null, state: null, country: null }
      let valid = true
      if (!addr || !addr.streetAddress1 || !addr.streetAddress1.trim()) {
        this.vendorAddressErrors.streetAddress1 = 'Street Address is required.'
        valid = false
      }
      if (!addr || !addr.city || !addr.city.trim()) {
        this.vendorAddressErrors.city = 'City is required.'
        valid = false
      }
      if (!addr || !addr.state) {
        this.vendorAddressErrors.state = 'State is required.'
        valid = false
      }
      if (!addr || !addr.country) {
        this.vendorAddressErrors.country = 'Country is required.'
        valid = false
      }
      return valid
    },
    doLoadPropertyGroups () {
      return this.getPropertyGroups().then(r => {
        this.propertyGroups = r.data.propertyGroups
      })
    },
    async SaveVendor (data) {
      const addressValid = this.validateVendorAddress()
      const fieldsValid = await this.$refs.formValidationObserver.validate()
      if (!addressValid || !fieldsValid) return
      const newVendor = this.vendorForEdit
      this.saveVendor(newVendor)
        .then(() => {
          this.$root.$emit('bv::refresh::table', 'vendors-table')
          this.toggleModal()
          this.$toasted.show('Vendor Saved.', {duration: 4000})
        })
    },
    addPropertyToList () {
      console.log('looking up property: ' + this.modalSelectedProperty)
      var foundProperty = this.lookupProperty(this.modalSelectedProperty)
      console.log('found: ' + foundProperty.name)
      if (this.vendorForEdit.propertyAssociations.find(p => p === this.modalSelectedProperty) != null) {
        return
      }
      this.vendorForEdit.propertyAssociations.push(foundProperty.id)
      this.vendorForEditProperties.push({id: foundProperty.id, city: foundProperty.address.city, name: foundProperty.name, currentUserAssociated: true, state: foundProperty.address.state})
    },
    addPropertyGroupToList () {
      console.log('looking up property: ' + this.selectedPropertyGroup)
      var foundPropertyGroup = this.lookupPropertyGroup(this.selectedPropertyGroup)
      console.log('found: ' + foundPropertyGroup.name)
      if (this.vendorForEdit.propertyGroupAssociations.find(p => p === this.selectedPropertyGroup) != null) {
        return
      }
      this.vendorForEdit.propertyGroupAssociations.push(foundPropertyGroup.id)
      this.vendorForEditPropertyGroups.push({id: foundPropertyGroup.id, name: foundPropertyGroup.name, currentUserAssociated: true})
    },
    deletePropertyAssociation (id) {
      // console.log('array before delete ' + JSON.stringify(this.personForEdit.propertyAssociations, null, 2))
      console.log('deleting property association: ' + id)
      this.vendorForEdit.propertyAssociations = this.vendorForEdit.propertyAssociations.filter((p, i, arr) => p !== id)
      // console.log('array after delete ' + JSON.stringify(this.personForEdit.propertyAssociations, null, 2))
      this.vendorForEditProperties = this.vendorForEditProperties.filter(
        (p, i, arr) => p.id !== id
      )
    },
    deletePropertyGroupAssociation (id) {
      console.log('deleting property Group association: ' + id)
      this.vendorForEdit.propertyGroupAssociations = this.vendorForEdit.propertyGroupAssociations.filter((p, i, arr) => p !== id)

      this.vendorForEditPropertyGroups = this.vendorForEditPropertyGroups.filter(
        (p, i, arr) => p.id !== id
      )
    },
    lookupProperty (id) {
      return this.allCompanyProperties.find(p => p.id === id)
    },
    lookupPropertyGroup (id) {
      return this.propertyGroups.find(p => p.id === id)
    },

    doLoadVendorTypes () {
      this.getVendorTypes().then(result => {
        console.log('Vendor Types:', result.data.types)
        this.vendorTypes = result.data.types
      })
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/views/setup/Vendors.vue